import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Position, Edge } from '@xyflow/react';
import { FlowNode, Topic } from '../components/types'
import axios from 'axios';
import { USER_SERVER } from '../components/Config.js';
import { Configuration } from "../api/configuration"
import * as Api from "../api/api"

axios.defaults.withCredentials = true;

const config = new Configuration({
  basePath: USER_SERVER, // Set your API base URL
});

const mapApi = new Api.MapApi(config);
const nodeApi = new Api.NodeApi(config);
const topicApi = new Api.TopicApi(config);
const userApi = new Api.UserApi(config);


interface FlowStore {
  isAuth: boolean;
  role: number;
  selectedNode: Api.NodeData | null; //this is funky need to check it out later
  nodes: FlowNode[];
  edges: Edge[];
  topics: Topic[];
  error: string | null;
  success: string | null;
  newTopic: string | null;
  userDetails: Api.User | null;
  viewedUserDetails: Api.User | null; // Add this property for the user being viewed on profile page
  postEdge: (topicId: string, edge: Api.Edge) => Promise<boolean>;
  deleteEdge: (topicId: string, edgeId: string) => Promise<boolean>;
  postTopic: (topic: Api.Topic) => Promise<{success: boolean, tid: string| null, nid: string| undefined}>;
  putTopic: (topicId: Topic) => Promise<void>;
  deleteTopic: (topicId: string) => Promise<boolean>;
  getTopics: () => Promise<void>;
  getMap: (topicId: string) => Promise<void>;
  auth:() => Promise<void>;
  logout:() => Promise<void>;
  putUser: (user: Api.User) => Promise<boolean>;
  getUser: (userId: string) => Promise<void>;
  getViewedUser: (userId: string) => Promise<void>;
  deleteUser: (userId: string) => Promise<void>;
  deleteNode: (nodeInfo: {nodeId: string, tid: string}) => Promise<boolean>;
  getNode: (nodeInfo: {nodeId: string, tid: string}) => Promise<void>;
  getNextNodeBattleTested: (nodeInfo: {nodeId: string, tid: string}) => Promise<string | undefined>;
  getNextNodeFresh: (nodeInfo: {nodeId: string, tid: string}) => Promise<string | undefined>;
  putNodeTitle: (newNode: Api.NodeData) => Promise<[boolean, boolean]>;
  putNodeVideoVote: (newNode: Api.NodeData) => Promise<void>;
  putNodeVideoEdit: (newNode: Api.NodeData) => Promise<boolean>;
  putNodeBattleVote: (newNode: Api.NodeData) => Promise<void>;
  putNodeFreshVote: (newNode: Api.NodeData) => Promise<void>;
  putNodeFlag: (newNode: Api.NodeData) => Promise<boolean>;
  postNode: (node: Api.NodeData) => Promise<{success: boolean, targetId: string | null, sourceId: string | null}>;
  clearError: () => void;
  clearSuccess: () => void;
  lastMap: string | null;
  setLastMap: (mapURL: string) => void;
}

export const emptyTopic: Topic = {
  id: "",
  title: ""
}

const handleError = (
  error: unknown,
  message: string,
  set: (
    partial: FlowStore | Partial<FlowStore> | ((state: FlowStore) => FlowStore | Partial<FlowStore>),
    replace?: false,
    action?: string
  ) => void
) => {
  console.error(message, error);
  let combinedMessage: string;

  if (axios.isAxiosError(error)) {
    combinedMessage = `${
      error.response?.data || "No Data"
    }: ${error.response?.status || "Unknown Status"}`;
  } else if (error instanceof Error) {
    combinedMessage = `${error.message} ${message}`;
  } else {
    combinedMessage = `Unknown error: ${String(error)}`;
  }

  set(
    (state) => ({
      ...state,
      error: `${message}: ${combinedMessage}`,
    }),
    false, // Explicitly set replace to `false`
    "flowStore/handleError" // Optional action name
  );
};

const handleSuccess = (
  message: string,
  set: (
    partial: FlowStore | Partial<FlowStore> | ((state: FlowStore) => FlowStore | Partial<FlowStore>),
    replace?: false,
    action?: string
  ) => void
) => {
  set(
    (state) => ({
      ...state,
      success: message,
    }),
    false, // Explicitly set replace to `false`
    "flowStore/handleSuccess" // Optional action name
  );
};



export const useFlowStore = create(
  devtools<FlowStore>((set, get) => ({
    isAuth: false,
    role: 0,
    lastMap: null,
    selectedNode: null,
    nodes: [],
    edges: [],
    topics: [],
    newTopic: null,
    error: null,
    success: null,
    userDetails: null,      // Currently authenticated user
    viewedUserDetails: null, // User being viewed on profile page

    setLastMap: (mapURL: string) => {
      set(
        (state) => ({
          ...state,
          lastMap: mapURL,
        }),
        false,
        "flowStore/setLastMap"
      );
    },

    clearError: () => {
      set(
        (state) => ({
          ...state,
          error: null,
        }),
        false,
        "flowStore/clearError"
      );
    },

    clearSuccess: () => {
      set(
        (state) => ({
          ...state,
          success: null,
        }),
        false,
        "flowStore/clearError"
      );
    },

    postEdge: async (topicId: string, edge: Api.Edge): Promise<boolean> => {
      try {
        const response = await mapApi.addEdge(topicId, edge)
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
        handleSuccess("Edge Posted", set)
        return true
      } catch (error) {
        handleError(error, "Error posting edge", set);
        return false
      }
    },

    deleteEdge: async (topicId: string, edgeId: string): Promise<boolean> => {
      try {
        const response = await mapApi.deleteEdge(topicId, edgeId)
        if (response.status !== 204) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
        handleSuccess("Edge Deleted", set)
        return true
      } catch (error) {
        handleError(error, "Error deleting edge", set);
        return false
      }
    },

    deleteTopic: async (topicID: string): Promise<boolean> => {
      try {
        const response = await topicApi.deleteTopic(topicID)
        if (response.status !== 204) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
        handleSuccess(topicID + " deleted", set)
        return true;
      } catch (error) {
        handleError(error, "Error deleting topic", set);
        return false;
      }
    },

    putTopic: async (topic: Topic) => {
      try {
        // const response = await axios.put(`${USER_SERVER}/topic`, topic);
        const response = await topicApi.updateTopic(topic)
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
      } catch (error) {
        handleError(error, "Error putting topic", set);
      }
    },

    postTopic: async (topic: Api.Topic): Promise<{ success: boolean; tid: string | null; nid: string | undefined }> => {
      // Clear any existing selected node data
      set({ selectedNode: null });

      try {
        const response = await topicApi.addTopic(topic)
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
        handleSuccess(topic.title + " added", set)
        return { success: true, tid: response.data.topic.title, nid: response.data.nodeData.id };
      } catch (error) {
        handleError(error, "Error posting topic", set);
        return { success: false, tid: null, nid: undefined };
      }
    },

    getTopics: async () => {
      try {
        const response = await topicApi.getTopics()
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
        set(
          (state) => ({
            ...state,
            topics: response.data,
          }),
          false,
          "flowStore/getTopics"
        );
      } catch (error) {
        handleError(error, "Error getting topics", set);
      }
    },

    getMap: async (topicId: string) => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      try {
        const response = await axios.get(`${USER_SERVER}/map/${topicId}`);
        // const response = await mapApi.getMapById(topicId)
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
        set(
          (state) => ({
            ...state,
            nodes: response.data.nodes.map((node: Api.FlowNode, index: number) => {
              // Calculate grid position based on index and screen dimensions
              const gridSize = Math.ceil(Math.sqrt(response.data.nodes.length)); // Grid size
              const cellWidth = Math.floor(width / gridSize);
              const cellHeight = Math.floor(height / gridSize);
              const row = Math.floor(index / gridSize);
              const col = index % gridSize;

              // Update node properties
              return {
                ...node,
                type: "flowNode",
                targetPosition: Position.Left,
                sourcePosition: Position.Right,
                position: {
                  x: col * cellWidth + cellWidth / 2, // Centered within cell
                  y: row * cellHeight + cellHeight / 2, // Centered within cell
                },
              };
            }),
            edges: response.data.edges,
          }),
          false,
          "flowStore/getMap"
        );
      } catch (error) {
        handleError(error, "Error getting map", set);
      }
    },

    auth: async () => {
      try {
        // Get current auth state
        const { isAuth } = get();

        // If we're already authenticated, no need to check again
        if (isAuth) {
          return;
        }
        // const response = await axios.get(`${USER_SERVER}/users/auth`);

        // Try to check authentication with the users/auth endpoint
        try {
          // Check authentication with users/auth endpoint

          // Parse cookies to get the flow_jwt token
          const cookieObj: Record<string, string> = document.cookie.split('; ').reduce((prev, current) => {
            const [name, value] = current.split('=');
            return { ...prev, [name]: value };
          }, {} as Record<string, string>);

          // Extract the flow_jwt cookie value
          const flowJwt = cookieObj['flow_jwt'];

          // Extract cookies for authentication

          // Always include the Authorization header with the JWT token if available
          const headers: Record<string, string> = {};
          if (flowJwt) {
            headers['Authorization'] = `Bearer ${flowJwt}`;
          } else if (cookieObj['JWT']) {
            headers['Authorization'] = `Bearer ${cookieObj['JWT']}`;
          }

          // Wrap the axios call in a try-catch block to handle any errors
          let response;
          try {
            response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/users/auth`, {
              withCredentials: true, // Important: include cookies in the request
              headers: headers
            });
          } catch (directError) {
            console.error('Error making direct request to backend:', directError);

            try {
              response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/users/auth`, {
                withCredentials: true,
                headers: headers
              });
            } catch (fallbackError) {
              // Try one more approach with a different URL format
              response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/users/auth`, {
                withCredentials: true,
                headers: headers,
                // Disable proxy to ensure direct connection
                proxy: false
              });
            }
          }

          if (response.data && response.data.isAuth) {

            // User is authenticated
            set(
              (state) => ({
                ...state,
                isAuth: true,
                role: response.data.role || 0,
                userDetails: {
                  username: response.data.name || '',
                  email: response.data.email || '',
                  id: response.data.id || ''
                }
              }),
              false,
              "flowStore/auth/success"
            );
            return;
          } else {
            // User is not authenticated
            set(
              (state) => ({
                ...state,
                isAuth: false,
                role: 0,
                userDetails: null
              }),
              false,
              "flowStore/auth/failure"
            );
            return;
          }
        } catch (error) {
          console.log('Auth check failed:', error);
          if (axios.isAxiosError(error)) {
            console.log('Error details:', error.response?.data || error.message);
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        set(
          (state) => ({
            ...state,
            isAuth: false,
            role: 0,
          }),
          false,
          "flowStore/auth/error"
        );
      }
    },

    logout: async () => {
      try {
        // await axios.post(`/auth/al/logout`);

        // First, call the backend logout endpoint
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/logout`, {
            method: 'GET',
            credentials: 'include' // Include cookies
          });

          // Try to parse the response body
          try {
            const data = await response.json();
          } catch (e) {
            console.log('No JSON response from logout endpoint');
          }
        } catch (fetchError) {
          console.error('Error calling logout endpoint:', fetchError);

          // Try a direct call to the backend as fallback
          try {
            const directResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/logout`, {
              method: 'GET',
              credentials: 'include' // Include cookies
            });
          } catch (directError) {
            console.error('Direct logout call also failed:', directError);
          }
        }

        // Clear the auth state regardless of backend response
        set(
          (state) => ({
            ...state,
            isAuth: false,
            role: 0,
            userDetails: null,
          }),
          false,
          "flowStore/logout"
        );

        // Clear localStorage
        localStorage.removeItem('auth_user');
        localStorage.removeItem('isAuthenticated');

        // Redirect to login page
        window.location.href = '/';
      } catch (error) {
        console.error('Error during logout:', error);
        handleError(error, "Error logging out", set);
      }
    },

    putUser: async (user: Api.User): Promise<boolean> => {
      try {
        // const response = await axios.put(`${USER_SERVER}/user`, user);
        const response = await userApi.updateUser(user)
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
        handleSuccess("User Modified", set)
        return true;
      } catch (error) {
        handleError(error, "Error putting user", set);
        return false;
      }
    },

    getUser: async (userId: string) => {
      try {
        const response = await userApi.getUserByName(userId)
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }

        set(
          (state) => ({
            ...state,
            userDetails: response.data,
          }),
          false,
          "flowStore/getUser"
        );
      } catch (error) {
        console.error('Error in getUser:', error);
        handleError(error, "Error getting user", set);
      }
    },

    getViewedUser: async (userId: string) => {
      try {
        const response = await userApi.getUserByName(userId)
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }

        set(
          (state) => ({
            ...state,
            viewedUserDetails: response.data,
          }),
          false,
          "flowStore/getViewedUser"
        );
      } catch (error) {
        console.error('Error in getViewedUser:', error);
        handleError(error, "Error getting user", set);
      }
    },

    deleteUser: async (userId: string) => {
      try {
        // const response = await axios.delete(`${USER_SERVER}/user/${userId}`);
        const response = await userApi.deleteUser(userId)
        if (response.status !== 204) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
        set(
          (state) => ({
            ...state,
            userDetails: null,
          }),
          false,
          "flowStore/deleteUser"
        );
      } catch (error) {
        handleError(error, "Error deleting user", set);
      }
    },

    putNodeTitle: async (newNode: Api.NodeData): Promise<[boolean, boolean]> => {
      try {
        const response = await nodeApi.updateNodeTitle(newNode);
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
        handleSuccess("Node Modified", set);
        return [true, response.data]; // [success, editorAdded]
      } catch (error) {
        handleError(error, "Error putting node", set);
        return [false, false]; // [failure, not added]
      }
    },

    putNodeVideoVote: async (newNode: Api.NodeData): Promise<void> => {
      try {
        const response = await nodeApi.updateNodeVideoVote(newNode);
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }

        // Update the store with the returned vote count without resorting
        set(
          (state) => ({
            ...state,
            selectedNode: state.selectedNode ? {
              ...state.selectedNode,
              youtubeLinks: state.selectedNode.youtubeLinks?.map(video =>
                video.link === newNode.youtubeLinks?.[0].link
                  ? { ...video, votes: response.data }
                  : video
              )
            } : null
          }),
          false,
          "flowStore/updateVideoVote"
        );
      } catch (error) {
        handleError(error, "Error putting video vote", set);
      }
    },

    putNodeVideoEdit: async (newNode: Api.NodeData): Promise<boolean> => {
      try {
        const response = await nodeApi.updateNodeVideoEdit(newNode);
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }

        const successMessage = "Link Action Successful";

        handleSuccess(successMessage, set);
        return true;
      } catch (error) {
        handleError(error, "Error updating video", set);
        return false;
      }
    },

    putNodeBattleVote: async (newNode: Api.NodeData): Promise<void> => {
      try {
        const response = await nodeApi.updateNodeBattleVote(newNode);
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
        // Update the store with the returned vote count
        set(
          (state) => ({
            ...state,
            selectedNode: state.selectedNode ? {
              ...state.selectedNode,
              battleTested: response.data
            } : null
          }),
          false,
          "flowStore/updateBattleVote"
        );
      } catch (error) {
        handleError(error, "Error putting node", set);
      }
    },

    putNodeFreshVote: async (newNode: Api.NodeData): Promise<void> => {
      try {
        const response = await nodeApi.updateNodeFreshVote(newNode);
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
        // Update the store with the returned vote count
        set(
          (state) => ({
            ...state,
            selectedNode: state.selectedNode ? {
              ...state.selectedNode,
              fresh: response.data
            } : null
          }),
          false,
          "flowStore/updateFreshVote"
        );
      } catch (error) {
        handleError(error, "Error putting node", set);
      }
    },

    putNodeFlag: async (newNode: Api.NodeData): Promise<boolean> => {
      try {
        const response = await nodeApi.updateNodeFlag(newNode);
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
        return true;
      } catch (error) {
        handleError(error, "Error putting node", set);
        return false;
      }
    },

    getNode: async (nodeInfo: { nodeId: string; tid: string }): Promise<void> => {
      set({ selectedNode: null }); // Clear the selected node before fetching a new one

      try {
        const response = await nodeApi.getNode(nodeInfo.nodeId, nodeInfo.tid);
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
        set({ selectedNode: response.data });
      } catch (error) {
        handleError(error, "Error getting node", set);
      }
    },

    getNextNodeBattleTested: async (nodeInfo: { nodeId: string; tid: string }): Promise<string | undefined> => {
      try {
        const response = await nodeApi.getNodeNextBattleTested(nodeInfo.nodeId, nodeInfo.tid);
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
        //I just want it to return the id to the caller
        return response.data;
      } catch (error) {
        handleError(error, "Error getting next node", set);
      }
    },

    getNextNodeFresh: async (nodeInfo: { nodeId: string; tid: string }): Promise<string | undefined> => {
      try {
        const response = await nodeApi.getNodeNextFresh(nodeInfo.nodeId, nodeInfo.tid);
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
        return response.data;
      } catch (error) {
        handleError(error, "Error getting next node", set);
      }
    },

    deleteNode: async (nodeInfo: { nodeId: string; tid: string }): Promise<boolean> => {
      try {
        // const response = await axios.delete(`${USER_SERVER}/node`, { params: nodeInfo });
        const response = await nodeApi.deleteNode(nodeInfo.nodeId, nodeInfo.tid)
        if (response.status !== 204) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
        set(
          (state) => ({
            ...state,
            selectedNode: null,
          }),
          false,
          "flowStore/deleteNode"
        );
        return true;
      } catch (error) {
        handleError(error, "Error deleting node", set);
        return false;
      }
    },

    postNode: async (node: Api.NodeData): Promise<{ success: boolean; sourceId: string | null; targetId: string | null }> => {
      try {
        // const response = await axios.post(`${USER_SERVER}/node`, node);
        const response = await nodeApi.addNode(node)
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }
        set(
          (state) => ({
            ...state,
            selectedNode: response.data as Api.NodeData | Api.NodeData | null, //this is funky need to check it out later
          }),
          false,
          "flowStore/postNode"
        );
        return { success: true, sourceId: response.data.sourceId, targetId: response.data.targetId };
      } catch (error) {
        handleError(error, "Error posting node", set);
        return { success: false, sourceId: null, targetId: null };
      }
    },

    updateUser: async (userData: any) => {
      try {
        // Ensure the id field is set correctly
        if (!userData.id && get().userDetails?.id) {
          userData.id = get().userDetails?.id;
        }
        
        console.log("Updating user with data:", userData);
        
        const response = await userApi.updateUser(userData);
        if (response.status !== 200) {
          throw new Error(`Error ${response.status}: ${response?.statusText || "Unknown error"}`);
        }

        // Refresh user details after update
        if (userData.id) {
          await get().getUser(userData.id);
        }
        
        return true;
      } catch (error) {
        console.error('Error in updateUser:', error);
        handleError(error, "Error updating user", set);
        return false;
      }
    }

  }), {
    name: 'FlowStore',
    enabled: true,
  })
);

